import { Fragment, useContext, useEffect, useRef, useState } from "react";
import { BreadCrumb } from "../../Layout/Common/breadCrumb";
import { isValidArray, normalizePath, normalizeUrlPath } from "../../Layout/Common/common";
import { CSSTransition, TransitionGroup } from "react-transition-group";
import { Link, useLocation, useParams } from "react-router-dom";
import { PAGES } from "../../routeConfig";
import { GlobalContext } from "../../Context/GlobalContext";
import { BrandGridContainer } from "./brandGrid";
import { ModelGridContainer } from "./modelGrid";
import { BrandModelPageMain } from "./brandModelPage";
import { usePageTitle } from "../../utils/pageTitle";

export const CompatibleBrandMain = () => {
  const [breadcrumb, setBreadcrumb] = useState([{ name: "Compatible Product", url: PAGES.BRANDMODEL }]);
  const [brandList, setBrandList] = useState([]);
  const [modelList, setModelList] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [selectedModel, setSelectedModel] = useState(null);

  const { compatibleList } = useContext(GlobalContext);
  const location = useLocation();

  // Deepest thing the URL points at wins: model, else brand, else the router's
  // "Compatible Products" placeholder.
  usePageTitle(selectedModel?.modelName ?? selectedBrand?.brandName);

  useEffect(() => {
    if (!isValidArray(compatibleList)) return;
    const path = location.pathname.replace(/^\/|\/$/g, "");
    const parts = path.split("/");

    // reset
    setSelectedBrand(null);
    setSelectedModel(null);
    setModelList([]);

    // /brands
    if (parts.length <= 1) {
      setBrandList(compatibleList);
      setBreadcrumb([{ name: "Compatible Product", url: PAGES.BRANDMODEL }]);
      return;
    }

    // /brands/:brand
    if (parts.length == 2) {
      const brandUrl = parts.join("/");
      const brand = compatibleList.find((b) => b.brandUrl == brandUrl);

      if (brand && isValidArray(brand.modelList)) {
        setSelectedBrand(brand);
        setModelList(brand.modelList ?? []);
        setBrandList(null);
        setBreadcrumb([
          { name: "Compatible Product", url: PAGES.BRANDMODEL },
          { name: brand.brandName, url: null },
        ]);
      }
      return;
    }

    // /brands/:brand/:model
    if (parts.length == 3) {
      const brandUrl = parts.slice(0, 2).join("/");
      const modelUrl = parts.join("/");

      const brand = compatibleList.find((b) => b.brandUrl == brandUrl);
      const model = brand?.modelList?.find((m) => m.modelUrl == modelUrl);

      if (brand) {
        setSelectedBrand(brand);
        setModelList(brand.modelList ?? []);
      }

      if (model) {
        setSelectedModel(model);
        setBreadcrumb([
          { name: "Compatible Product", url: PAGES.BRANDMODEL },
          { name: brand.brandName, url: "/" + brand.brandUrl },
          { name: model.modelName, url: "/" + model.modelUrl },
        ]);
      }
    }
  }, [compatibleList, location.pathname]);

  const handleBrandSelect = (brand) => {
    if (!brand) return;

    setSelectedBrand(brand);
    setModelList(brand.modelList ?? []);
    setSelectedModel(null);
  };

  const handleModelSelect = (model) => {
    if (!model) return;
    setSelectedModel(model);
    setBreadcrumb([
      { name: "Compatible Product", url: PAGES.BRANDMODEL },
      { name: selectedBrand?.brandName ?? "", url: selectedBrand?.brandUrl ?? "" },
      { name: model.modelName, url: model.modelUrl ?? "" },
    ]);
  };

  return (
    <Fragment>
      <BreadCrumb data={breadcrumb} />
      {isValidArray(compatibleList) ? (
        <Fragment>
          <CSSTransition in={isValidArray(brandList) && !selectedModel} timeout={500} classNames="fade" unmountOnExit>
            <div className="row transition-wrapper results-min-height">
              <BrandGridContainer brands={brandList} handleBrandSelected={handleBrandSelect} />
            </div>
          </CSSTransition>
          <CSSTransition in={isValidArray(modelList) && !selectedModel} timeout={500} classNames="fade" unmountOnExit>
            <div className="row transition-wrapper results-min-height">
              <ModelGridContainer models={modelList} handleModelSelected={handleModelSelect} />
            </div>
          </CSSTransition>
          <CSSTransition in={selectedModel} timeout={500} classNames="fade" unmountOnExit>
            <div className="row transition-wrapper results-min-height">
              {selectedModel && <BrandModelPageMain model={selectedModel} />}
            </div>
          </CSSTransition>
        </Fragment>
      ) : (
        <BrandGridContainer brands={null} handleBrandSelected={handleBrandSelect} />
      )}
    </Fragment>
  );
};
